import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Info, ArrowRight, Save, Loader2, CheckCircle2, Lock, PlayCircle,
  Scale, FileText, Layers, MapPinned, Building2, ShieldCheck, AlertTriangle, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaSelect from "@/components/jea/JeaSelect";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import BrokerResultCard from "@/components/queries/BrokerResultCard";
import ConsolidatedAssetSummary from "@/components/queries/ConsolidatedAssetSummary";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney } from "@/lib/pricing";
import { BUNDLES, detectBundleFromSlugs } from "@/lib/bundles";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { mapJudgmentCourtLevelToDiscovery } from "@/utils/courtLevelMapping";
import { cn } from "@/lib/utils";

const COURT_LEVELS = [
  { key: "small_claims", label: "Small Claims Court" },
  { key: "county_circuit", label: "County or Circuit Court" },
  { key: "federal", label: "Federal or Higher Court" },
];

const GUIDANCE = {
  small_claims: {
    heading: "Hearing in Aid of Execution",
    body: (
      <>
        <p>
          A Hearing in Aid of Execution (sometimes "Debtor's Exam" or "Order for Examination")
          is a court-ordered hearing where the debtor must appear and answer questions under
          oath about their income, assets, and ability to pay. This is the primary discovery
          tool in small claims practice.
        </p>
        <p>
          <strong className="text-white">How to request:</strong> file a Motion to Compel Debtor Appearance
          (or the equivalent state form), serve the debtor by personal service or certified mail,
          and attend the hearing with your subpoena of records.
        </p>
        <p>
          <strong className="text-white">Escalation:</strong> if the debtor fails to appear, request an Order
          to Show Cause. If ignored, the court may find the debtor in contempt and issue a bench warrant.
        </p>
      </>
    ),
    checklist: [
      "File a Motion to Compel Debtor Appearance",
      "Serve debtor at least 10 days before hearing",
      "Prepare a list of asset questions (see Priority Questions below)",
      "Bring judgment order + court docket + ID",
    ],
    questions: [],
  },
  county_circuit: {
    heading: "Post-Judgment Interrogatories and Depositions",
    body: (
      <>
        <p>
          County and Circuit courts allow two powerful discovery mechanisms after judgment:
          <strong className="text-white"> written interrogatories</strong> (typically 25-30 questions
          the debtor must answer in writing under oath, with a 30-day response window) and
          <strong className="text-white"> depositions</strong> (oral examination under oath, in person,
          with a court reporter).
        </p>
        <p>
          <strong className="text-white">Service:</strong> personal service or certified mail per Rule 4.
          Interrogatories are typically served with a subpoena duces tecum requesting supporting
          documents (bank statements, pay stubs, tax returns).
        </p>
        <p>
          <strong className="text-white">Non-response:</strong> Motion to Compel, then sanctions.
        </p>
      </>
    ),
    checklist: [
      "Draft interrogatories using the Priority Questions below",
      "Serve with subpoena duces tecum for supporting documents",
      "Give the debtor 30 days to respond",
      "If no response, file Motion to Compel + sanctions",
    ],
    questions: [
      "All employers, current and previous 5 years",
      "All bank accounts including credit unions and online banks",
      "Real property owned individually or jointly",
      "Vehicles, boats, aircraft",
      "Business ownership interests (LLCs, partnerships, corporations)",
      "Retirement and investment accounts (with amounts)",
      "Cryptocurrency holdings",
      "Anticipated inheritances, settlements, or lawsuit proceeds",
    ],
  },
  federal: {
    heading: "Federal Post-Judgment Discovery (Fed. R. Civ. P. 69)",
    body: (
      <>
        <p>
          Rule 69 imports state discovery mechanisms into federal proceedings AND allows
          federal-level tools like nationwide subpoenas. Once you have a federal judgment,
          you can compel discovery from the debtor and third parties (banks, employers,
          brokerages) anywhere in the United States.
        </p>
        <p>
          <strong className="text-white">Typical sequence:</strong> file the writ of execution first,
          then serve Rule 69 discovery. Cross-reference PACER to identify prior filings, other
          judgments against this debtor, and any bankruptcy history.
        </p>
        <p>
          <strong className="text-white">Enforcement:</strong> non-compliance is contempt of a
          federal court order — meaningful consequences including fines and incarceration.
        </p>
      </>
    ),
    checklist: [
      "File writ of execution with the federal clerk",
      "Serve Rule 69 interrogatories on debtor",
      "Serve subpoenas duces tecum on known third parties (banks, employer)",
      "Check PACER for co-pending filings",
    ],
    questions: [],
  },
};


const CourtLevelSelector = ({
  value,
  onChange,
  autoDetectedKey,
  showMissingWarning,
  showResetLink,
  onReset,
}) => (
  <JeaCard data-testid="discovery-court-level">
    {showMissingWarning && (
      <div
        className="mb-3 flex items-start gap-2 p-3 rounded-[8px] border border-warning/40 bg-warning/10"
        data-testid="discovery-court-level-missing-warning"
      >
        <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
        <div className="text-text-secondary text-[12px] leading-5">
          This judgment's court level isn't set &mdash; select below to see appropriate guidance.
        </div>
      </div>
    )}
    <div className="flex items-center justify-between mb-3">
      <div className="jea-caption">Court Level</div>
      {showResetLink && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 text-[11px] text-accent-pink hover:text-accent-pink-light transition-colors"
          data-testid="discovery-court-level-reset"
          title="Switch back to the court level inferred from this judgment"
        >
          <RotateCcw size={11} /> Reset to judgment default
        </button>
      )}
    </div>
    <div className="grid grid-cols-3 gap-0 rounded-[8px] overflow-hidden border border-border-default">
      {COURT_LEVELS.map((l, i) => (
        <button
          key={l.key}
          type="button"
          onClick={() => onChange(l.key)}
          className={cn(
            "h-11 text-[13px] font-semibold transition-colors",
            i > 0 && "border-l border-border-default",
            value === l.key
              ? "bg-accent-pink text-bg-primary font-bold"
              : "bg-bg-primary text-text-secondary hover:bg-bg-tertiary"
          )}
          data-testid={`court-level-${l.key}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  </JeaCard>
);


const GuidancePanel = ({ courtLevel }) => {
  const g = GUIDANCE[courtLevel] || GUIDANCE.small_claims;
  const levelLabel = COURT_LEVELS.find((c) => c.key === courtLevel)?.label || "";
  return (
    <JeaCard data-testid="discovery-guidance">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-white font-bold text-[16px]">Discovery Guidance — {levelLabel}</h2>
        <span
          className="inline-flex items-center gap-1 text-text-secondary hover:text-accent-pink text-[11px]"
          title="General procedural guidance. Statutes and rules vary by jurisdiction. Consult local counsel."
          data-testid="discovery-info-tooltip"
        >
          <Info size={12} /> General guidance
        </span>
      </div>

      <div className="text-accent-pink text-[13px] font-semibold tracking-[0.06em] uppercase mb-3">
        {g.heading}
      </div>

      <div className="space-y-3 text-text-secondary text-[14px] leading-6">{g.body}</div>

      {g.checklist?.length > 0 && (
        <div className="mt-5" data-testid="discovery-checklist">
          <div className="jea-caption mb-2">Practical Checklist</div>
          <ol className="list-decimal list-inside space-y-1 text-text-secondary text-[13px]">
            {g.checklist.map((c, i) => <li key={i}>{c}</li>)}
          </ol>
        </div>
      )}

      {g.questions?.length > 0 && (
        <div className="mt-5" data-testid="discovery-priority-questions">
          <div className="jea-caption mb-2">Priority Questions</div>
          <ol className="list-decimal list-inside space-y-1 text-text-secondary text-[13px]">
            {g.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}

      <div className="mt-5 text-text-muted italic text-[12px]">Not legal advice.</div>
    </JeaCard>
  );
};


const AssetSearchPanel = ({
  brokers, bundles, selectedSlugs, setSelectedSlugs, pickedBundle, setPickedBundle,
  running, onRun, priceMap, disc, subtotal, discount, total, accountType, canRun,
}) => {
  const { inPreview } = usePreviewGate();

  const toggleSlug = (slug) => {
    setSelectedSlugs((cur) => {
      const next = cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug];
      // Deselect bundle if selection no longer matches
      setPickedBundle(null);
      return next;
    });
  };

  const applyBundle = (bundleKey) => {
    setPickedBundle(bundleKey);
    const b = BUNDLES.find((x) => x.key === bundleKey);
    setSelectedSlugs(b?.slugs || []);
  };

  // Detect passive-match bundle from current custom selection
  const passiveBundle = useMemo(() => {
    if (pickedBundle) return pickedBundle;
    return detectBundleFromSlugs(selectedSlugs);
  }, [pickedBundle, selectedSlugs]);

  return (
    <JeaCard data-testid="discovery-asset-search">
      <h2 className="text-white font-bold text-[16px]">Run Asset Search</h2>
      <p className="text-text-secondary text-[13px] mt-1">
        Query one or more data brokers to surface assets subject to enforcement.
        Same 7-broker engine used elsewhere in the platform — pre-populated with your default preferences.
      </p>

      {/* Bundle chips */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="discovery-bundle-chips">
        {BUNDLES.map((b) => {
          const Icon = b.Icon;
          const active = passiveBundle === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => applyBundle(b.key)}
              data-testid={`discovery-bundle-${b.key}`}
              className={cn(
                "flex items-start gap-3 p-3 rounded-[10px] border transition-all text-left",
                active
                  ? "border-accent-pink bg-accent-pink/10"
                  : "border-border-default bg-bg-primary hover:border-accent-pink/50"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                active ? "bg-accent-pink text-bg-primary" : "bg-bg-tertiary text-accent-pink"
              )}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={cn("font-bold text-[13px]", active ? "text-accent-pink" : "text-white")}>
                  {b.label}
                </div>
                <div className="text-text-secondary text-[11px]">{b.slugs.length} brokers</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Broker cards */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="discovery-broker-grid">
        {brokers.map((b) => {
          const checked = selectedSlugs.includes(b.slug);
          const price = priceMap[b.slug] ?? b.price_cents;
          return (
            <label
              key={b.id}
              className={cn(
                "flex items-start gap-3 p-3 rounded-[8px] border cursor-pointer transition-colors",
                checked
                  ? "border-accent-pink bg-bg-tertiary"
                  : "border-border-default bg-bg-primary hover:border-accent-pink/40"
              )}
              data-testid={`discovery-broker-${b.slug}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleSlug(b.slug)}
                className="mt-1 accent-accent-pink"
                data-testid={`discovery-broker-checkbox-${b.slug}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-white font-semibold text-[13px]">{b.name}</div>
                  {accountType === "pro" && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent-pink/15 text-accent-pink font-bold border border-accent-pink/40">
                      PRO
                    </span>
                  )}
                </div>
                <div className="text-text-secondary text-[11px] mt-0.5 line-clamp-2">{b.description}</div>
              </div>
              <div className="jea-mono text-accent-pink font-bold text-[13px] whitespace-nowrap">
                {formatMoney(price)}
              </div>
            </label>
          );
        })}
      </div>

      {/* Cost summary */}
      <div className="mt-4 bg-bg-primary border border-border-default rounded-[8px] p-4"
           data-testid="discovery-cost-summary">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-text-secondary">Subtotal</span>
          <span className="jea-mono text-white font-semibold">{formatMoney(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className="flex items-center justify-between text-[13px] mt-1">
            <span className="text-success">Bundle discount</span>
            <span className="jea-mono text-success font-semibold">− {formatMoney(discount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-default">
          <span className="text-white font-bold text-[14px]">Total</span>
          <span className="jea-mono text-accent-pink font-extrabold text-[18px]" data-testid="discovery-total">
            {formatMoney(total)}
          </span>
        </div>
      </div>

      {canRun && (
        <JeaButton
          onClick={onRun}
          loading={running}
          disabled={selectedSlugs.length === 0}
          fullWidth
          size="lg"
          className="mt-4 gap-2"
          data-testid="discovery-run"
        >
          <PlayCircle size={18} /> Run Selected Searches
        </JeaButton>
      )}
    </JeaCard>
  );
};


const ResultsPanel = ({ brokerResults, revealed }) => {
  if (brokerResults.length === 0) return null;
  return (
    <JeaCard data-testid="discovery-results">
      <h2 className="text-white font-bold text-[16px] mb-4">Search Results</h2>
      <div className="space-y-3">
        {brokerResults.map((r, i) => (
          <AnimatePresence key={r.broker_slug}>
            {revealed[i] ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28 }}
              >
                <BrokerResultCard result={r} />
              </motion.div>
            ) : (
              <div className="h-24 rounded-[8px] bg-bg-primary/60 border border-border-default overflow-hidden relative">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-accent-pink/10 to-transparent"
                  style={{ animation: "shimmer 1.5s infinite" }}
                />
              </div>
            )}
          </AnimatePresence>
        ))}
      </div>
    </JeaCard>
  );
};


const DiscoveryPage = () => {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { guardAction, inPreview } = usePreviewGate();
  const { canMutateDebtors, canRunQueries } = useRolePermissions();

  // Accept `judgment_id` as the preferred URL param; fall back to `debtor_id`
  // for backward compatibility with pre-existing deep-links (e.g. the
  // Enforcement workspace emits `?debtor_id=...` historically).
  const initialJudgmentId =
    searchParams.get("judgment_id") || searchParams.get("debtor_id") || "";

  const [debtors, setDebtors] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [prefs, setPrefs] = useState({}); // broker_id → enabled
  const [me, setMe] = useState(null);
  const [selectedDebtor, setSelectedDebtor] = useState(initialJudgmentId);
  const [courtLevel, setCourtLevel] = useState("county_circuit");
  // Resolved mapping for the currently-selected judgment's court_level.
  // Null when no judgment is selected (manual-only mode).
  const [judgmentDefaultCourtLevel, setJudgmentDefaultCourtLevel] = useState(null);
  // True when the user manually clicks a different court-level pill AFTER
  // auto-detect has resolved one from the judgment. Drives the reset link.
  const [manualCourtLevelOverride, setManualCourtLevelOverride] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState([]);
  const [pickedBundle, setPickedBundle] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null); // { query_run, report }
  const [revealed, setRevealed] = useState({});
  const [savingReport, setSavingReport] = useState(false);
  const [loading, setLoading] = useState(true);

  // Boot: load debtors, brokers, and my user
  useEffect(() => {
    (async () => {
      try {
        const [dRes, bRes, meRes] = await Promise.all([
          apiClient.get("/debtors"),
          apiClient.get("/brokers"),
          apiClient.get("/auth/me"),
        ]);
        const _debtors = (dRes.data.debtors || []).filter((d) => d.status !== "archived");
        setDebtors(_debtors);
        setBrokers(bRes.data.brokers || []);
        setMe(meRes.data);
        // Build prefs map from user_enabled flag
        const prefMap = {};
        (bRes.data.brokers || []).forEach((b) => {
          prefMap[b.id] = b.user_enabled !== false;
        });
        setPrefs(prefMap);

        // If URL has debtor_id, auto-select; else default to first
        if (!selectedDebtor && _debtors[0]) setSelectedDebtor(_debtors[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a debtor is chosen, pre-populate default broker selection + court-level
  useEffect(() => {
    if (!selectedDebtor || brokers.length === 0) return;
    const d = debtors.find((x) => x.id === selectedDebtor);
    if (!d) return;
    // Resolve the judgment's inferred court level via the shared mapping utility.
    // We always compute this (even when court_level is blank) so the warning
    // strip / default-key logic has a single source of truth.
    const mapped = mapJudgmentCourtLevelToDiscovery(d.judgment?.court_level);
    setJudgmentDefaultCourtLevel(mapped);
    setCourtLevel(mapped.key);
    setManualCourtLevelOverride(false);
    // Preselect brokers using user prefs (default: all enabled)
    const defaults = brokers
      .filter((b) => prefs[b.id] !== false)
      .map((b) => b.slug);
    setSelectedSlugs(defaults);
    setPickedBundle(null);
    setRunResult(null);
    setRevealed({});
    // Keep the URL in sync using the preferred `judgment_id` param.
    // We also strip any legacy `debtor_id` so the URL has a single source of truth.
    setSearchParams((s) => {
      const n = new URLSearchParams(s);
      n.set("judgment_id", selectedDebtor);
      n.delete("debtor_id");
      return n;
    });
  }, [selectedDebtor, brokers, prefs]);  // eslint-disable-line

  // Wrap the raw setter so manual court-level clicks flip the override flag.
  const handleCourtLevelChange = (nextKey) => {
    setCourtLevel(nextKey);
    if (
      judgmentDefaultCourtLevel &&
      nextKey !== judgmentDefaultCourtLevel.key
    ) {
      setManualCourtLevelOverride(true);
    } else {
      setManualCourtLevelOverride(false);
    }
  };

  const resetCourtLevelToJudgmentDefault = () => {
    if (!judgmentDefaultCourtLevel) return;
    setCourtLevel(judgmentDefaultCourtLevel.key);
    setManualCourtLevelOverride(false);
  };

  // Price map (PRO rate applied)
  const priceMap = useMemo(() => {
    const m = {};
    brokers.forEach((b) => { m[b.slug] = b.price_cents; });
    return m;
  }, [brokers]);

  const { subtotal, discount, total } = useMemo(() => {
    const sel = brokers.filter((b) => selectedSlugs.includes(b.slug));
    const sub = sel.reduce((acc, b) => acc + (b.price_cents || 0), 0);
    // Bundle discount: 10% if the current selection exactly matches any bundle
    const match = detectBundleFromSlugs(selectedSlugs);
    const disc = match ? Math.round(sub * 0.10) : 0;
    return { subtotal: sub, discount: disc, total: sub - disc };
  }, [brokers, selectedSlugs]);

  const runSearch = guardAction(async () => {
    if (!selectedDebtor) { toast.error("Choose a judgment first."); return; }
    if (selectedSlugs.length === 0) { toast.error("Select at least one broker."); return; }
    setRunning(true);
    setRunResult(null);
    setRevealed({});
    try {
      const bundleKey = detectBundleFromSlugs(selectedSlugs);
      const body = {
        debtor_id: selectedDebtor,
        court_level: courtLevel,
        ...(bundleKey ? { bundle: bundleKey } : { broker_slugs: selectedSlugs }),
      };
      const { data } = await apiClient.post("/discovery/search", body);
      setRunResult(data);
      // Sequentially reveal broker cards
      const brokerRes = data.query_run?.broker_results || [];
      brokerRes.forEach((_, i) => {
        setTimeout(() => setRevealed((r) => ({ ...r, [i]: true })), 200 + i * 400);
      });
      toast.success("Discovery complete.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  }, { feature: "running discovery searches" });

  const goEnforcement = () => {
    // Keep the legacy `debtor_id` param for the enforcement page which still
    // reads it; also pass `judgment_id` so once Enforcement migrates it's a
    // no-op there. Discovery and Enforcement treat them as synonyms.
    nav(`/enforcement?debtor_id=${selectedDebtor}&judgment_id=${selectedDebtor}&from_discovery=1`);
  };

  const saveAsReport = async () => {
    // The report was auto-generated by the discovery service. Just toast + link.
    if (!runResult?.report?.id) return;
    setSavingReport(true);
    try {
      toast.success("Discovery report saved. Access it in Reports.");
      setTimeout(() => nav(`/reports/${runResult.report.id}`), 800);
    } finally { setSavingReport(false); }
  };

  const currentDebtor = debtors.find((d) => d.id === selectedDebtor);

  return (
    <div className="space-y-6" data-testid="discovery-page">
      <div className="space-y-1">
        <div className="jea-caption">Enforcement Tools</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Discovery</h1>
      </div>

      {loading ? (
        <Skeleton className="h-24" />
      ) : debtors.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No judgments yet"
          description={canMutateDebtors ? "Add a judgment to begin discovery." : "No judgments are visible for your role."}
          testid="discovery-empty"
          footer={canMutateDebtors ? <JeaButton onClick={() => nav("/debtors/new")}>Add Judgment</JeaButton> : null}
        />
      ) : (
        <>
          <JeaCard data-testid="discovery-debtor-picker-card">
            <div className="jea-caption mb-2">Choose a Judgment</div>
            <JeaSelect
              value={selectedDebtor}
              onChange={setSelectedDebtor}
              options={debtors.map((d) => ({
                value: d.id,
                label: `${d.full_name}${d.judgment?.case_number ? ` · ${d.judgment.case_number}` : ""}`,
              }))}
              data-testid="discovery-debtor-picker"
            />
          </JeaCard>

          {selectedDebtor && (
            <>
              <CourtLevelSelector
                value={courtLevel}
                onChange={handleCourtLevelChange}
                autoDetectedKey={judgmentDefaultCourtLevel?.key}
                showMissingWarning={!!judgmentDefaultCourtLevel && !judgmentDefaultCourtLevel.isRecognized}
                showResetLink={!!judgmentDefaultCourtLevel && manualCourtLevelOverride}
                onReset={resetCourtLevelToJudgmentDefault}
              />
              <GuidancePanel courtLevel={courtLevel} />

              <AssetSearchPanel
                brokers={brokers}
                selectedSlugs={selectedSlugs}
                setSelectedSlugs={setSelectedSlugs}
                pickedBundle={pickedBundle}
                setPickedBundle={setPickedBundle}
                running={running}
                onRun={runSearch}
                priceMap={priceMap}
                subtotal={subtotal}
                discount={discount}
                total={total}
                accountType={me?.account_type}
                canRun={canRunQueries}
              />

              <ResultsPanel
                brokerResults={runResult?.query_run?.broker_results || []}
                revealed={revealed}
              />

              {runResult?.report && (
                <>
                  <ConsolidatedAssetSummary
                    summary={runResult.report.summary}
                    consolidated={runResult.report.consolidated_assets}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="discovery-actions">
                    <JeaButton onClick={goEnforcement} size="lg" className="gap-2" data-testid="discovery-goto-enforcement">
                      Go to Enforcement Workspace <ArrowRight size={16} />
                    </JeaButton>
                    <JeaButton
                      variant="outline-pink" size="lg"
                      onClick={saveAsReport}
                      loading={savingReport}
                      className="gap-2"
                      data-testid="discovery-save-report"
                    >
                      <Save size={16} /> View Discovery Report
                    </JeaButton>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DiscoveryPage;
